import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../../context/UserContext';
import { useChat } from './ChatContext';
import ChannelList from './ChannelList';
import ChannelHeader from './ChannelHeader';
import MessageThread from './MessageThread';
import ThreadPanel from './ThreadPanel';
import { useChatChannels } from './useChatChannels';
import { Icon, IconButton } from '../ui';

export default function ChatPanel() {
  const { user } = useUser();
  const {
    isChatOpen,
    closeChat,
    selectedChannelId,
    setSelectedChannelId,
    isSidebarCollapsed,
    toggleSidebar,
    activeThreadId,
    closeThread,
  } = useChat();
  const { channels } = useChatChannels();
  const [isMobile, setIsMobile] = useState(false);
  const [showChannelList, setShowChannelList] = useState(true);

  // Track mobile breakpoint
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile, when a channel is selected, show messages
  useEffect(() => {
    if (selectedChannelId && isMobile) {
      setShowChannelList(false);
    }
  }, [selectedChannelId, isMobile]);

  const location = useLocation();

  // Don't render on the fullscreen chat page or for unauthenticated users
  if (location.pathname.startsWith('/chat')) return null;
  if (!user) return null;

  const selectedChannel = channels.find(ch => ch.id === selectedChannelId);

  const handleBack = () => {
    setShowChannelList(true);
    setSelectedChannelId(null);
  };

  return (
    <>
      {/* Chat panel */}
      <AnimatePresence>
        {isChatOpen && (
          <>
            {/* Mobile backdrop */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeChat}
                className="fixed inset-0 z-40"
                style={{ background: 'var(--surface-overlay)' }}
              />
            )}

            <motion.div
              initial={isMobile ? { y: '100%' } : { x: '100%' }}
              animate={isMobile ? { y: 0 } : { x: 0 }}
              exit={isMobile ? { y: '100%' } : { x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className={`fixed z-50 flex ${
                isMobile
                  ? 'inset-0 top-0'
                  : 'right-0 top-0 bottom-0 w-[420px]'
              }`}
              style={{
                background: 'var(--surface-card)',
                borderLeft: '1px solid var(--border-hairline)',
                borderTopLeftRadius: isMobile ? 0 : 'var(--radius-lg)',
                borderBottomLeftRadius: isMobile ? 0 : 'var(--radius-lg)',
                boxShadow: 'var(--shadow-pop)',
                overflow: 'hidden',
                // full-screen on a phone: keep the header out of the notch and
                // the composer above the home indicator
                ...(isMobile ? {
                  paddingTop: 'var(--sat)',
                  paddingBottom: 'var(--sab)',
                  paddingLeft: 'var(--sal)',
                  paddingRight: 'var(--sar)',
                } : {}),
              }}
            >
              {/* Mobile: either channel list or messages */}
              {isMobile ? (
                showChannelList ? (
                  <div className="flex flex-col w-full h-full">
                    {/* Mobile header */}
                    <div
                      className="flex items-center justify-between px-4 py-3"
                      style={{ borderBottom: '1px solid var(--border-hairline)' }}
                    >
                      <h2
                        style={{
                          margin: 0,
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                          color: 'var(--text-body)',
                        }}
                      >
                        Chat
                      </h2>
                      <IconButton icon="x" size="sm" label="Close chat" onClick={closeChat} />
                    </div>
                    <ChannelList />
                  </div>
                ) : activeThreadId ? (
                  <div className="flex flex-col w-full h-full relative">
                    <div
                      className="flex items-center px-4 py-3"
                      style={{ borderBottom: '1px solid var(--border-hairline)' }}
                    >
                      <IconButton
                        icon="chevron-left"
                        size="sm"
                        label="Back"
                        onClick={closeThread}
                        style={{ marginRight: '10px' }}
                      />
                      <h3
                        className="truncate"
                        style={{
                          margin: 0,
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                          color: 'var(--text-body)',
                        }}
                      >
                        Thread in #{selectedChannel?.name || 'channel'}
                      </h3>
                      <IconButton
                        icon="x"
                        size="sm"
                        label="Close chat"
                        onClick={closeChat}
                        style={{ marginLeft: 'auto' }}
                      />
                    </div>
                    <ThreadPanel threadId={activeThreadId} onClose={closeThread} />
                  </div>
                ) : selectedChannel ? (
                  <div className="flex flex-col w-full h-full relative">
                    <ChannelHeader
                      channel={selectedChannel}
                      onClose={closeChat}
                      onBack={handleBack}
                      isMobile
                    />
                    <MessageThread channelId={selectedChannel.id} />
                  </div>
                ) : (
                  <div className="flex flex-col w-full h-full">
                    <ChannelList />
                  </div>
                )
              ) : (
                /* Desktop: side-by-side layout */
                <>
                  {/* Channel list sidebar — collapsible */}
                  <div className="relative flex-shrink-0 h-full">
                    <motion.div
                      animate={{ width: isSidebarCollapsed ? 0 : 150 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="h-full overflow-hidden"
                      style={{ borderRight: '1px solid var(--border-hairline)' }}
                    >
                      <div className="w-[150px] h-full">
                        <ChannelList />
                      </div>
                    </motion.div>

                    {/* Collapse/expand toggle button */}
                    <button
                      onClick={toggleSidebar}
                      title={isSidebarCollapsed ? 'Show channels' : 'Hide channels'}
                      className="hover-wash pressable absolute top-1/2 -translate-y-1/2 -right-3 z-10 flex items-center justify-center"
                      style={{
                        width: '24px',
                        height: '24px',
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-hairline)',
                        borderRadius: 'var(--radius-pill)',
                        color: 'var(--text-muted)',
                        boxShadow: 'var(--shadow-rest)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon
                        name={isSidebarCollapsed ? 'chevron-right' : 'chevron-left'}
                        size={12}
                      />
                    </button>
                  </div>

                  {/* Message area or Thread */}
                  <div className="flex-1 flex flex-col min-w-0 relative h-full">
                    {activeThreadId ? (
                      <>
                        <div
                          className="flex items-center px-3 py-2"
                          style={{ borderBottom: '1px solid var(--border-hairline)' }}
                        >
                          <IconButton
                            icon="chevron-left"
                            size="sm"
                            label="Back to channel"
                            onClick={closeThread}
                            style={{ marginRight: '8px' }}
                          />
                          <h3
                            className="truncate"
                            style={{
                              margin: 0,
                              fontSize: 'var(--text-xs)',
                              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                              color: 'var(--text-body)',
                            }}
                          >
                            Thread in #{selectedChannel?.name || 'channel'}
                          </h3>
                          <IconButton
                            icon="x"
                            size="sm"
                            label="Close chat"
                            onClick={closeChat}
                            style={{ marginLeft: 'auto' }}
                          />
                        </div>
                        <ThreadPanel threadId={activeThreadId} onClose={closeThread} />
                      </>
                    ) : selectedChannel ? (
                      <>
                        <ChannelHeader
                          channel={selectedChannel}
                          onClose={closeChat}
                          onToggleSidebar={toggleSidebar}
                          isSidebarCollapsed={isSidebarCollapsed}
                        />
                        <MessageThread channelId={selectedChannel.id} />
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        {/* Close button in empty state */}
                        <IconButton
                          icon="x"
                          size="sm"
                          label="Close chat"
                          onClick={closeChat}
                          style={{ position: 'absolute', top: '10px', right: '10px' }}
                        />
                        <Icon
                          name="messages-square"
                          size={36}
                          className="mb-3"
                          style={{ color: 'var(--text-faint)', opacity: 0.5 }}
                        />
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                          Select a channel
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
